import RestEndpointsEnum from "../models/RestEndpointsEnum";
import { buildRestUrl, buildRestUrlWithQuery, buildAuthHeadersForFetch } from "./RestService";
import IUserUI from "../models/IUserUI";
import ISupplierModel from "../models/ISupplierModel";
import IVendorModel from "../models/IVendorModel";
import IGetFavoritesDTO from "../models/DTO/IGetFavoritesDTO";
import IOnGetFavoritesDTO from "../models/DTO/IOnGetFavoritesDTO";
import IOnGetFavoriteIdsDTO from "../models/DTO/IOnGetFavoriteIdsDTO";
import IAddFavoriteDTO from "../models/DTO/IAddFavoriteDTO";
import IOnAddFavoriteDTO from "../models/DTO/IOnAddFavoriteDTO";
import IBulkAddFavoritesDTO from "../models/DTO/IBulkAddFavoritesDTO";
import IOnBulkAddFavoritesDTO from "../models/DTO/IOnBulkAddFavoritesDTO";
import IOnRemoveFavoriteDTO from "../models/DTO/IOnRemoveFavoriteDTO";

/** Server page cap for GET /v1/favorites (CatalogItemFavoriteService.FAVORITES_MAX_PAGE_SIZE). */
export const FAVORITES_MAX_PAGE_SIZE = 100;
/** Server cap for POST /v1/favorites/bulk (CatalogItemFavoriteService.FAVORITES_MAX_BULK_SIZE). */
export const FAVORITES_MAX_BULK_SIZE = 200;

/**
 * Ecommerce favorites — the customer account's self-curated reorder list (OR-1447 API).
 *
 * The list belongs to the customer account (vendorGuid header), so every call needs the vendor
 * model for portal (non-supplier) users; buildAuthHeadersForFetch throws otherwise.
 */
const handleResponse = async <T>(response: Response, fallback: string): Promise<T> => {
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || response.statusText || fallback);
    }

    return (await response.json()) as T;
};

/** Lightweight toggle state: every favorite whose item still exists (no availability filtering). */
export const getFavoriteIds = async (
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<IOnGetFavoriteIdsDTO> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.FavoritesGetIds });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel, vendorModel });

    const response = await fetch(url, { method: "GET", headers });

    return handleResponse<IOnGetFavoriteIdsDTO>(response, "Failed to load favorites");
};

/** The favorites list in the storefront card shape, newest first, availability-filtered server-side. */
export const getFavorites = async (
    options: IGetFavoritesDTO,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<IOnGetFavoritesDTO> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.FavoritesGet,
        query: { limit: options.limit, offset: options.offset },
    });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel, vendorModel });

    const response = await fetch(url, { method: "GET", headers });

    return handleResponse<IOnGetFavoritesDTO>(response, "Failed to load favorites");
};

/** Idempotent add. */
export const addFavorite = async (
    catalogItemGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<IOnAddFavoriteDTO> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.FavoritesAdd });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel, vendorModel });
    const body: IAddFavoriteDTO = { catalogItemGuid };

    const response = await fetch(url, { method: "POST", headers, body: JSON.stringify(body) });

    return handleResponse<IOnAddFavoriteDTO>(response, "Failed to add favorite");
};

/** Idempotent remove. */
export const removeFavorite = async (
    catalogItemGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<IOnRemoveFavoriteDTO> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.FavoritesRemove, params: { catalogItemGuid } });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel, vendorModel });

    const response = await fetch(url, { method: "DELETE", headers });

    return handleResponse<IOnRemoveFavoriteDTO>(response, "Failed to remove favorite");
};

/** Idempotent bulk add ("add from recent orders"). Guids beyond the server cap are dropped client-side. */
export const bulkAddFavorites = async (
    catalogItemGuids: string[],
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<IOnBulkAddFavoritesDTO> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.FavoritesBulkAdd });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel, vendorModel });
    const body: IBulkAddFavoritesDTO = { catalogItemGuids: catalogItemGuids.slice(0, FAVORITES_MAX_BULK_SIZE) };

    const response = await fetch(url, { method: "POST", headers, body: JSON.stringify(body) });

    return handleResponse<IOnBulkAddFavoritesDTO>(response, "Failed to add favorites");
};

const FavoritesService = {
    getFavoriteIds,
    getFavorites,
    addFavorite,
    removeFavorite,
    bulkAddFavorites,
};

export default FavoritesService;
